import React, {
  Fragment,
  useCallback,
  useState,
  useEffect,
  useMemo,
} from "react";
import { Menu, Dropdown, Layout, Radio, Input, Tooltip } from "antd";
import { withRouter } from "react-router-dom";
import { LogoutOutlined, WalletOutlined } from "@ant-design/icons";
import SettingsIcon from "@material-ui/icons/Settings";
import MenuIcon from "@material-ui/icons/Menu";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import Avatar from "react-avatar";
import Brightness3Icon from "@material-ui/icons/Brightness3";
import Brightness5Icon from "@material-ui/icons/Brightness5";
import { useSiderMenuToggleModel } from "../models/useToggle";
import { defaultThemeId, themes, useAntdTheme } from "../theming";
import { connect, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import LiveCalls from "../components/LiveCall";
import { useMediaQuery } from "react-responsive";
import { useCount } from "../context/liveContext";
import { useSocketContext } from "../utils/SocketContext.js";

const { Header } = Layout;
const options = themes.map((theme) => ({
  label: theme.displayName,
  value: theme.id,
}));
const AppHeader = (props) => {
  let { loginOut, Tenant, liveDetails } = props;
  let history = useHistory();
  const socket = useSocketContext();
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const activeTheme = useSelector((state) => state.loader.theme);
  const auth = useSelector((state) => state.auth.authenticated);
  const role_id = parseInt(auth.role, 10);
  const url = new URL(window.location.href);
  var pathSegments = url.pathname.split("/");
  const tenantId = pathSegments[2];
  const key = `tenant_${tenantId}`;
  const viewTenant = url.pathname.indexOf("view-tenant") > -1;
  const selectedTenants = JSON.parse(localStorage.getItem(key)) || null;
  const Balance = useSelector((state) => state.auth.balance);
  const menuCollapsed = useSiderMenuToggleModel();
  const [tenants, setTenants] = useState([]);
  const [filteredTenants, setFilteredTenants] = useState([]);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [dropdownHeading, setDropdownHeading] = useState("View Tenant");
  const [livedata, setLiveData] = useState({});
  const [themeId, setThemeId] = useState(defaultThemeId);
  useAntdTheme(themeId);
  const selectThemeId = useCallback(
    (option) => setThemeId(option.target.value),
    []
  );
  const {
    setTotalDeviceCCCount,
    setTotalDevice6Count,
    setTotalDeviceUserCount,
    setWaitingCount,
  } = useCount();

  useEffect(() => {
    socket.on("realtime", function (data) {
      setLiveData(data);
    });
  }, [socket]);

  //livedetails and contactcenter count
  const tenantList = useMemo(() => {
    return Object.values(liveDetails || {}).map((tenant) => {
      const ccDetails = Object.values(tenant.tenant_prop || {}).map((cc) => ({
        cc_id: cc.cc_id,
        cc_name: cc.cc_name,
        cc_extension: cc.cc_extension,
        agents: cc.cc_prop || [],
      }));

      return {
        tenant_id: tenant.tenant_id,
        tenant_name: tenant.tenant_name,
        cc: ccDetails,
        agents: ccDetails.flatMap((cc) => cc.agents),
      };
    });
  }, [Tenant]);

  const calculateTotalCounts = () => {
    let totalDeviceCount = 0;
    let totalWaitingCount = 0;

    const processTenantData = (liveTenantData) => {
      if (liveTenantData && liveTenantData.devices?.[4]) {
        const activeDevices = liveTenantData.devices[4].filter(
          (device) => device.channelstatedesc === "Up"
        );
        totalDeviceCount += activeDevices.length;
      }
      if (liveTenantData && liveTenantData.waitings) {
        totalWaitingCount = liveTenantData.waitings.length || 0;
      }
    };

    if (tenantList?.length > 0 && !viewTenant) {
      tenantList.forEach((tenant) => {
        const tenantId = tenant.tenant_id;
        const liveTenantData = livedata?.heart?.[tenantId];
        processTenantData(liveTenantData);
      });
    } else if (viewTenant && selectedTenants) {
      const tenantId = selectedTenants.id;
      const liveTenantData = livedata?.heart?.[tenantId];
      processTenantData(liveTenantData);
    } else if (role_id === 3) {
      const liveTenantData = livedata?.heart;
      processTenantData(liveTenantData);
    }

    return { totalDeviceCount, totalWaitingCount };
  };

  useEffect(() => {
    const { totalDeviceCount, totalWaitingCount } = calculateTotalCounts();
    setTotalDeviceCCCount(totalDeviceCount);
    setWaitingCount(totalWaitingCount);
  }, [tenantList, role_id, viewTenant, selectedTenants, livedata]);

  //livestatus  and user count and pro count
  const calculateAllDeviceCounts = (
    Tenant,
    livedata,
    viewTenant,
    selectedTenants,
    role_id
  ) => {
    let device6Count = 0;
    let device12Count = 0;

    const calculateCounts = (liveTenantData) => {
      if (liveTenantData && liveTenantData.devices?.[6]) {
        device6Count += liveTenantData.devices[6].length;
      }
      if (liveTenantData && liveTenantData.devices) {
        const devices1 = liveTenantData.devices?.["1"] || [];
        const devices2 = liveTenantData.devices?.["2"] || [];
        const allDevices = [...devices1, ...devices2];
        device12Count += allDevices.length;
      }
    };

    if (Tenant?.length > 0 && !viewTenant) {
      Tenant?.forEach((tenant) => {
        const tenantId = tenant.id;
        const liveTenantData = livedata?.heart?.[tenantId];
        calculateCounts(liveTenantData);
      });
    } else if (viewTenant && selectedTenants) {
      const tenantId = selectedTenants.id;
      const liveTenantData = livedata?.heart?.[tenantId];
      calculateCounts(liveTenantData);
    } else if (role_id === 3) {
      const liveTenantData = livedata?.heart;
      calculateCounts(liveTenantData);
    }

    return { device6Count, device12Count };
  };

  useEffect(() => {
    const { device6Count, device12Count } = calculateAllDeviceCounts(
      Tenant,
      livedata,
      viewTenant,
      selectedTenants,
      role_id
    );
    setTotalDevice6Count(device6Count);
    setTotalDeviceUserCount(device12Count);
  }, [
    selectedTenant,
    livedata,
    tenantList,
    role_id,
    viewTenant,
    selectedTenants,
  ]);

  //APP HEADER CODE

  const handleMenuClick = (item) => {
    try {
      if (item.id === auth.id) {
        const win = window.open("/dashboard", "_blank");
        win.focus();
      } else {
        setSelectedTenant(item);
        const key = `tenant_${item.id}`;
        localStorage.setItem(key, JSON.stringify(item));
        const win = window.open(`/view-tenant/${item.id}/dashboard`, "_blank");
        win.focus();
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleSearch = (value) => {
    setSearchValue(value);
  };
  useEffect(() => {
    setTenants(Tenant);
  }, [Tenant]);

  useEffect(() => {
    const filteredList =
      searchValue.trim() === ""
        ? tenants
        : tenants?.filter((tenant) =>
            tenant.first_name.toLowerCase().includes(searchValue.toLowerCase())
          );
    setFilteredTenants(filteredList);
  }, [searchValue, tenants]);

  useEffect(() => {
    const key = `tenant_${tenantId}`;
    const storedTenant = localStorage.getItem(key);
    if (storedTenant) {
      setSelectedTenant(JSON.parse(storedTenant));
    }
  }, []);

  const renderHeaderContent = () => {
    if (selectedTenant) {
      return (
        <Dropdown
          overlay={menus}
          className="w-100 text-end"
          trigger={["click"]}
        >
          <div className="ant-dropdown-link menu-toggle-icon-center">
            <a
              className="ant-dropdown-link "
              style={{ fontWeight: "500", wordBreak: "break-word" }}
              onClick={(e) => e.preventDefault()}
            >
              {selectedTenant.first_name + " " + selectedTenant.last_name}
            </a>
          </div>
        </Dropdown>
      );
    } else {
      return (
        <Dropdown
          overlay={menus}
          className="w-100 text-end"
          trigger={["click"]}
        >
          <div className="ant-dropdown-link menu-toggle-icon-center">
            <a
              className="ant-dropdown-link"
              style={{ fontWeight: "500", wordBreak: "break-word" }}
              onClick={(e) => e.preventDefault()}
            >
              {dropdownHeading}
            </a>
          </div>
        </Dropdown>
      );
    }
  };

  useEffect(() => {
    const key = `tenant_${tenantId}`;
    const storedTenant = localStorage.getItem(key);
    if (storedTenant) {
      setSelectedTenant(JSON.parse(storedTenant));
    }
    const url = new URL(window.location.href);
    var pathSegments = url.pathname.split("/");
    var viewTenant = pathSegments[1];
    const selectedTenant = JSON.parse(localStorage.getItem(key));
    const tenantRoleId = parseInt(selectedTenant?.role, 10);

    if (viewTenant === "view-tenant") {
      if (tenantRoleId === 3) {
        const tenant = selectedTenant
          ? tenants?.find((t) => t.id === selectedTenant.id)
          : null;
        setDropdownHeading(selectedTenant.first_name);
      } else {
        setDropdownHeading("View Tenant");
      }
    } else {
      if (role_id !== 2) {
        setDropdownHeading(auth.username);
      } else {
        setDropdownHeading(auth.username);
      }
    }
  }, [tenants, auth, role_id]);

  const menus = (
    <Menu style={{ maxHeight: "400px", overflowY: "auto", width: "300PX" }}>
      {/* <Menu.Item>
    <Form.Item
              name="tenant_id"
              // label="Tenant"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Select
                showArrow
                showSearch 
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                placeholder="Select your Tenant"
                allowClear
                style={{
                  width: "100%",
                }}
              >
              <Option key={auth.id} value={auth.id}>
                    {auth.username}
                  </Option>
                {tenants?.((r, i) => (
                  <Option key={r.id} value={r.id}>
                    {r.username}
                  </Option>
                ))}
              </Select>
            </Form.Item>
    </Menu.Item> */}
      <div
        style={{ position: "sticky", top: 0, zIndex: 1, background: "#333" }}
      >
        <Input.Search
          placeholder="Search"
          value={searchValue}
          onChange={(e) => handleSearch(e.target.value)}
          onSearch={(value) => handleSearch(value)}
          enterButton
          allowClear
        />
      </div>
      <Menu.Divider />
      {window.location.pathname.includes("/view-tenant/") ? (
        <>
          <Menu.Item
            key={auth.id}
            onClick={() => handleMenuClick(auth)}
            style={{ fontWeight: "900" }}
          >
            {auth.username}
          </Menu.Item>
          <Menu.Divider />
        </>
      ) : (
        ""
      )}
      {filteredTenants?.map((tenant) => (
        <Menu.Item key={tenant.id} onClick={() => handleMenuClick(tenant)}>
          {tenant.first_name}
        </Menu.Item>
      ))}
    </Menu>
  );
  const menu = (
    <Menu>
      <Fragment>
        {/* <Menu.Item
                title="switch user"
                key={"switch"}
                style={{ cursor: "default" }}
              >
                <span style={{ fontStyle: "italic", marginLeft: 2 }}>
                  switch user
                </span>
              </Menu.Item> */}

        {/* <Menu.Item
          title="User"
          key={auth.uid}
          onClick={() => activateUser(x.id, x.role_id)}
        >
          <div className={auth.id ? "font-weight-bold" : ""}>
            &nbsp;
            <UserOutlined /> &nbsp; &nbsp;
            {auth?.name?.toUpperCase()}
          </div>
        </Menu.Item> */}
        <Menu.Divider />
        {!window.location.pathname.includes("/view-tenant/") ? (
          <Menu.Item>
            {" "}
            <div onClick={() => history.push("/personal-setting")}>
              &nbsp;
              <SettingsIcon /> &nbsp; Settings
            </div>
          </Menu.Item>
        ) : (
          ""
        )}

        <Menu.Divider />
        <Menu.Item key={"logout"}>
          <div onClick={loginOut}>
            &nbsp;
            <LogoutOutlined /> &nbsp; Logout
          </div>
        </Menu.Item>
        <Menu.Divider />
        <Menu.Item key={"themes"}>
          <div>
            &nbsp;
            <Radio.Group
              onChange={selectThemeId}
              buttonStyle="solid"
              className="theme-select"
            >
              {options.map((theme, index) => (
                <Radio.Button
                  className={
                    theme.value === activeTheme
                      ? "ant-radio-button-wrapper-checked"
                      : ""
                  }
                  value={theme.value}
                  key={theme.value}
                >
                  <span className="theme-icon">
                    {index === 0 ? (
                      <Brightness3Icon className="theme-icon" />
                    ) : (
                      <Brightness5Icon className="theme-icon" />
                    )}
                  </span>
                  {theme.label}
                </Radio.Button>
              ))}
            </Radio.Group>
            &nbsp; &nbsp;
          </div>
        </Menu.Item>
      </Fragment>
    </Menu>
  );

  return (
    <Fragment>
      <Header className="header b-bottom-dark">
        <div className="left menu-toggle-icon-center">
          {menuCollapsed.state ? (
            <ArrowBackIcon
              style={{ fontSize: 18 }}
              onClick={menuCollapsed.set(false)}
            />
          ) : (
            <MenuIcon
              style={{ fontSize: 18 }}
              onClick={menuCollapsed.set(true)}
            />
          )}
          {!isMobile ? (
            <div className="ml-3">
              <LiveCalls />
            </div>
          ) : (
            ""
          )}
        </div>
        <div className="right">
          {role_id === 2 && viewTenant ? (
            <div className="mr-3">
              <div>{renderHeaderContent()}</div>
            </div>
          ) : (
            " "
          )}
          <Tooltip
          // placement="left"
          //  title={status}
          >
            {/* <FiberManualRecordIcon
              className="pt-2"
              style={{ color: iconColor }}
            /> */}
            <WalletOutlined
              className="header-icon mr-1 "
              style={{ width: "20px", height: "20px" }}
            />
            <span style={{ fontWeight: "700" }} className="mr-2">
              $ {Balance ? parseFloat(Balance).toFixed(2) : 0}
            </span>
          </Tooltip>
          <div>
            <Dropdown overlay={menu} placement="bottomRight">
              <div className="ant-dropdown-link menu-toggle-icon-center">
                <Avatar
                  name={props.userinfo ? props.userinfo.username : "user"}
                  size="28"
                  textSizeRatio={1.75}
                />
              </div>
            </Dropdown>
          </div>
        </div>
      </Header>
      {isMobile ? (
        <Header
          style={{
            borderBottom: "1px solid #D9D9D9",
            paddingTop: "6px",
            height: "45px",
          }}
        >
          <div className="ml-3">
            <LiveCalls livedata={props.livedata} />
          </div>
        </Header>
      ) : (
        ""
      )}
    </Fragment>
  );
};
function mapStateToProps({ auth }) {
  return {
    userinfo: auth.authenticated,
  };
}
export default withRouter(connect(mapStateToProps)(AppHeader));
